/**
 * Indian digit grouping: 1,23,45,678 rather than 12,345,678.
 *
 * Not cosmetic. A lakh-grouped number read as a million-grouped one is off by
 * two orders of magnitude at a glance, which is the whole job of a screen.
 */
const INR = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const INR_CRORE = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 });

export function formatInr(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return INR.format(value);
}

/** Rupees at scale, in the units an Indian reader actually uses. */
export function formatInrCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const crore = 10_000_000;
  const lakh = 100_000;
  if (Math.abs(value) >= crore) return `${INR_CRORE.format(value / crore)} Cr`;
  if (Math.abs(value) >= lakh) return `${INR_CRORE.format(value / lakh)} L`;
  return INR.format(value);
}

/**
 * A price, always to the paisa.
 *
 * `formatInr` drops trailing zeros, which is right for a turnover and wrong
 * for a column of prices: "1,143" above "1,425.2" does not align on the
 * decimal, and a column of prices that does not align cannot be read down.
 */
const INR_PRICE = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return INR_PRICE.format(value);
}

/**
 * Whole crore, grouped the Indian way: Reliance reads "17,89,063".
 *
 * Crore rather than an abbreviation that switches unit down the column.
 * "17.9L Cr" above "4,457 Cr" above "892 Cr" cannot be compared by eye, and
 * comparing sizes by eye is the entire reason the column is there. Screener.in
 * shows the same thing the same way, which is what an Indian reader already
 * knows how to read.
 *
 * No decimals: a crore is already 1e7 rupees, so the paisa on a mid-cap is
 * eleven digits of false precision.
 */
const WHOLE_CRORE = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

/** One crore, in rupees. Mirrors `CRORE` in `@savi/core`. */
const CRORE = 10_000_000;

/**
 * A market cap in rupees, shown in crore.
 *
 * An em dash for a name we cannot size, which is 39% of the symbols that
 * produce signals — the same dash every other unknown in this table uses, and
 * the same thing the Unknown chip in the Size filter selects.
 */
export function formatCrore(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const crore = value / CRORE;
  // A company worth under a crore would round to a bare "0", which reads as an
  // error rather than as a very small number — and sorts against real values
  // instead of to the bottom where the unsized ones go.
  if (crore > 0 && crore < 1) return "<1";
  return WHOLE_CRORE.format(crore);
}

export function formatPercentile(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(0)}`;
}

export function formatNumber(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value.toFixed(digits);
}

export function formatBps(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${value.toFixed(1)} bps`;
}

/** 1 is the bottom decile, 10 the top. */
export function decileOf(percentile: number | null | undefined): number | null {
  if (percentile === null || percentile === undefined || !Number.isFinite(percentile)) return null;
  return Math.min(10, Math.floor(percentile * 10) + 1);
}

/**
 * India runs a fixed +05:30 offset with no daylight saving, which is true
 * here and nowhere else in the codebase.
 *
 * Mirrors `sessionDateInIst` in `packages/core/src/market/session-date.ts`.
 * The web app does not depend on `@savi/core`, and duplicating six lines
 * beats pulling a Node-shaped package into the browser bundle.
 */
const IST_OFFSET_MINUTES = 330;

/**
 * The IST calendar day an instant falls on.
 *
 * Slicing the first ten characters off an ISO instant files a 19:00 UTC
 * filing on the wrong day: 19:00 UTC is 00:30 IST tomorrow, and that is the
 * D1 defect (spec §2.2) wearing a display-formatting hat.
 */
export function istDate(instant: string | Date | null | undefined): string {
  const shifted = shiftToIst(instant);
  if (!shifted) return "—";
  const year = shifted.getUTCFullYear();
  const month = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  const day = String(shifted.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** The same instant to the minute, so a reader in India sees their own clock. */
export function istDateTime(instant: string | Date | null | undefined): string {
  const shifted = shiftToIst(instant);
  if (!shifted) return "—";
  const hours = String(shifted.getUTCHours()).padStart(2, "0");
  const minutes = String(shifted.getUTCMinutes()).padStart(2, "0");
  return `${istDate(instant)} ${hours}:${minutes} IST`;
}

/**
 * Minutes past midnight IST, for comparing an instant against a schedule.
 *
 * An unreadable instant returns NaN, which no window contains, so a caller
 * ends up saying "nothing is scheduled" rather than inventing a 00:00.
 */
export function istMinutesOfDay(instant: Date): number {
  const shifted = shiftToIst(instant);
  return shifted ? shifted.getUTCHours() * 60 + shifted.getUTCMinutes() : Number.NaN;
}

/** The IST day of the week, 0 = Sunday, so a caller can skip the weekend. */
export function istWeekday(instant: Date): number {
  const shifted = shiftToIst(instant);
  return shifted ? shifted.getUTCDay() : Number.NaN;
}

function shiftToIst(instant: string | Date | null | undefined): Date | null {
  if (instant === null || instant === undefined) return null;
  const at = typeof instant === "string" ? new Date(instant) : instant;
  if (Number.isNaN(at.getTime())) return null;
  return new Date(at.getTime() + IST_OFFSET_MINUTES * 60_000);
}

/**
 * A signed percentage, so a loss reads as one at a glance.
 *
 * The sign is explicit on the upside too: "+1.8%" beside "-0.4%" scans as a
 * column of returns, where "1.8%" scans as a magnitude.
 */
export function formatSignedPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${value >= 0 ? "+" : ""}${value.toFixed(digits)}%`;
}

/** Unsigned, for a distance rather than a direction: risk, ATR, reward. */
export function formatPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

/** Risk multiples read "+1.4R", because R is the unit that makes them comparable. */
export function formatR(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}R`;
}

/** The clock, without the date. The table already groups by day. */
export function istClock(instant: string | Date | null | undefined): string {
  const full = istDateTime(instant);
  return full === "—" ? full : (full.split(" ")[1] ?? full);
}

/**
 * How long ago, coarsely.
 *
 * An hourly signal is stale in a way a rank crossing is not, and "4h" beside
 * "2d" is the fastest way to see which rows are still live.
 */
export function formatAge(instant: string | Date | null | undefined, now: Date): string {
  const at = typeof instant === "string" ? new Date(instant) : instant;
  if (!at || Number.isNaN(at.getTime())) return "—";
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);
  if (minutes < 0) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
